/**
 * Product Domain Bridge Left Sidebar
 * 10-step progress list for Product Domain Bridge flow.
 * @feature PRODUCT_DOMAIN_BRIDGE_UI - Product-specific sidebar
 */

import React from 'react';
import { List, Typography, Space } from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  LockOutlined,
} from '@ant-design/icons';
import {
  ProductDomainStep,
  PRODUCT_DOMAIN_STEPS,
} from '../../types/bridgeSession';
import styles from './LeftProgressSidebar.module.css';

const { Text } = Typography;

const STEP_LABELS: Record<ProductDomainStep, string> = {
  product_context: 'Product Context',
  product_mechanics: 'Product Mechanics',
  current_performance: 'Current Performance',
  baseline_trajectory: 'Baseline',
  future_target: 'Future Target',
  product_entropy: 'Product Entropy',
  product_heat_map: 'Heat Map',
  bridge_focus: 'Bridge Focus',
  friction_priorities: 'Friction Priorities',
  define_transition: 'Define Transition',
  bridge_card: 'Bridge Card',
  control_activation: 'Control',
};

type StepStatus = 'complete' | 'in_progress' | 'locked';

interface ProductDomainLeftSidebarProps {
  currentStep: ProductDomainStep;
  onNavigate: (step: ProductDomainStep) => void;
}

function getStepStatus(
  step: ProductDomainStep,
  currentStep: ProductDomainStep
): StepStatus {
  const idx = PRODUCT_DOMAIN_STEPS.indexOf(step);
  const currentIdx = PRODUCT_DOMAIN_STEPS.indexOf(currentStep);
  if (step === currentStep) return 'in_progress';
  if (idx < currentIdx) return 'complete';
  return 'locked';
}

function canNavigateToStep(step: ProductDomainStep, currentStep: ProductDomainStep): boolean {
  const idx = PRODUCT_DOMAIN_STEPS.indexOf(step);
  const currentIdx = PRODUCT_DOMAIN_STEPS.indexOf(currentStep);
  return idx >= 0 && currentIdx >= 0 && idx <= currentIdx;
}

const getStatusIcon = (state: StepStatus) => {
  switch (state) {
    case 'complete':
      return <CheckCircleOutlined className={styles.statusIconComplete} />;
    case 'in_progress':
      return <ClockCircleOutlined className={styles.statusIconInProgress} />;
    case 'locked':
      return <LockOutlined className={styles.statusIconLocked} />;
    default:
      return null;
  }
};

const getStatusText = (state: StepStatus) => {
  switch (state) {
    case 'complete':
      return 'Complete';
    case 'in_progress':
      return 'In Progress';
    case 'locked':
      return 'Locked';
    default:
      return '';
  }
};

export const ProductDomainLeftSidebar: React.FC<ProductDomainLeftSidebarProps> = ({
  currentStep,
  onNavigate,
}) => {
  return (
    <div className={styles.sidebar}>
      <div className={styles.header}>
        <Text strong>Product Bridge</Text>
      </div>
      <List
        className={styles.list}
        dataSource={PRODUCT_DOMAIN_STEPS}
        renderItem={(step) => {
          const state = getStepStatus(step, currentStep);
          const canNavigate = canNavigateToStep(step, currentStep);
          const isCurrent = step === currentStep;
          const statusIcon = getStatusIcon(state);
          const statusText = getStatusText(state);

          return (
            <List.Item
              key={step}
              className={`${styles.sidebarItem} ${isCurrent ? styles.currentItem : ''} ${canNavigate ? styles.clickable : styles.disabled}`}
              onClick={() => canNavigate && onNavigate(step)}
            >
              <Space>
                {statusIcon}
                <Text className={styles.itemLabel}>{STEP_LABELS[step]}</Text>
                {statusText && (
                  <Text type="secondary" className={styles.statusText}>
                    {statusText}
                  </Text>
                )}
              </Space>
            </List.Item>
          );
        }}
      />
    </div>
  );
};
